import { withDevtools } from "@angular-architects/ngrx-toolkit";
import { computed, inject } from "@angular/core";
import { Adjustment, AdjustmentsListRequest, CreateAdjustmentDto } from "@dtos/quality/adjustment.item";
import { IncidencesListRequest, IncidentItemList } from "@dtos/quality/incident.item";
import { IncidentRequestDto, IncidentRequestTimelineDto } from "@dtos/quality/incident.request.dto";
import { IncidenceSubtype } from "@dtos/quality/incidents.dto";
import { IncidenceMainType } from "@enums/incidences.enum";
import { patchState, signalStore, withComputed, withMethods, withState } from "@ngrx/signals";
import { IncidencesProvider } from "@providers2/incidences.provider";
import { QualityProvider } from "@providers2/quality.provider";
import { Helpers } from "@utils/helpers";
import { BlackListDriverItem } from "@dtos/quality/blackList";
import { TransportPrincipalType } from "@enums/client.enum";
import { IncidentsMetricDto } from "@dtos/quality/incidents.metric.dto";
import { AdjustmentType } from "@enums/additional.enum";

export class QualityState {
    incidents: IncidentItemList[] = [];
    adjustments: Adjustment[] = [];
    incidentView: IncidentRequestDto | null = null;
    incidentOriginalView: IncidentRequestDto | null = null;
    adjustmentView: Adjustment | null = null;
    adjustmentOriginalView: Adjustment | null = null;
    subtypes: IncidenceSubtype[] = [];
    currentBlackListDriver: BlackListDriverItem[] = [];
    incidentsMetrics: IncidentsMetricDto | null = null;
}


export const QualityStore = signalStore(
    {
        providedIn: 'root',
        // Configuración para Redux DevTools
        ...(typeof window !== 'undefined' && (window as any).__REDUX_DEVTOOLS_EXTENSION__ && {
            devtools: {
                name: 'RoadIt Quality Store',
                trace: true,
                traceLimit: 25
            }
        })
    },
    withDevtools('QualityStore'),
    withState(new QualityState()),

    withMethods((store) => {
        return {
            updateState: (updates: Partial<QualityState>) => {
                patchState(store, { ...updates });
                saveQualityStoreToStorage(store);

            },
            initializeFromStorage: () => {
                var loadedStore = loadQualityStoreFromStorage();
                if (loadedStore) {
                    patchState(store, loadedStore);
                }

            },

            clearQuality: () => {
                patchState(store, new QualityState());
            },
        }
    }),

    withMethods((store) => {
        var provider = inject(QualityProvider);
        var incidencesProvider = inject(IncidencesProvider);
        
        return {
            loadIncidents: async (request?: IncidencesListRequest) => {
                try {
                    var res:any = await provider.getIncidents(request);
                    if (res) {
                        store.updateState({ incidents: res["results"] });
                        return {rows: res["count"], total_pages: res["total_pages"]};
                        
                    }else{
                        store.updateState({ incidents: [] })
                        return {rows: 0, total_pages: 0}

                    }
                } catch (error) {
                    console.log(error);
                    return {rows: 0, total_pages: 0}

                }
            },

            loadAdjustments: async (searchOptions?: AdjustmentsListRequest) => {
                try {
                    var res:any = await provider.getAdjustments(searchOptions);
                    if (res) {
                        store.updateState({ adjustments: res["results"] })
                        return {rows: res["count"], total_pages: res["total_pages"]}
                    }else{
                        store.updateState({ adjustments: [] })
                        return {rows: 0, total_pages: 0}
                        
                    }
                } catch (error) {
                    console.log(error);
                    return {rows: 0, total_pages: 0}
                }
            },
            loadSubtypes: async (mainType: string) => {
                if(store.incidentView() == null) {
                    store.updateState({ subtypes: [] });
                    return;
                }
                if (mainType === IncidenceMainType.DRIVER_RECOGNITION || mainType === IncidenceMainType.OTHER) {
                    store.updateState({subtypes: []});
                
                    return;
                }

                
                try {
                const result = await incidencesProvider.getSubtypes(mainType);
                store.updateState({subtypes: result || []});
                } catch (error) {
                console.error('Error al cargar subtipos:', error);
                store.updateState({subtypes: []});
                } 
            },              
            loadIncidentById: async (id: number) => {
                try {
                    const res = await provider.getIncidentById(id);
                    if (res) {
                        store.updateState({ incidentView: IncidentRequestDto.fromJson(res) , incidentOriginalView: IncidentRequestDto.fromJson(res) });
                        if(store.incidentView()?.driver?.id){
                            let result = await provider.getBlackListByDriver(store.incidentView()!.driver!.id);
                            if(!result || result.length == 0){
                                result = [];
                            }
                            store.updateState({ currentBlackListDriver: result });
                        }
                        provider.getIncidentsMetrics(id).then((res) => {
                            if (res) {
                                store.updateState({ incidentsMetrics: res });
                            }
                        })
                    }
                } catch (error) {
                    console.log(error);
                }
                
            },
            loadAdjustmentById: async (id: number) => {
                try {
                    const res = await provider.getAdjustmentById(id);
                    if (res) {
                        store.updateState({ adjustmentView: Adjustment.fromJson(res) , adjustmentOriginalView: Adjustment.fromJson(res) });
                        return;
                    }
                    throw new Error("No se pudo obtener el ajuste");
                } catch (error) {
                    store.updateState({ adjustmentView: null , adjustmentOriginalView: null });
                    throw error;
                }
                
            },

            incidentUpdate: (incident: Partial<IncidentRequestDto>) => {
                store.updateState({ incidentView: IncidentRequestDto.fromJson({ ...store.incidentView(), ...incident }) });
            },
            compareChangesIncident: (): {} => {
                const current = store.incidentView();
                const original = store.incidentOriginalView();
                if (!current || !original) return {};

                const changes: any = {};
                
                const scalarKeys = [
                    'comments', 
                    'dont_make_adjustment',
                    'incidence_main_type', 
                    'incidence_sub_type', 
                    'rating_modification', 
                    'status', 
                    'responsibility', 
                    'severity', 
                    'client_zendesk_link', 
                    'driver_zendesk_link', 
                    'amount', 
                    'notice_driver', 
                    'in_client_blacklist', 
                    'exclude_by_client_movement_type'
                ];

                scalarKeys.forEach(key => {
                    if (current[key as keyof IncidentRequestDto] !== original[key as keyof IncidentRequestDto]) {
                        changes[key] = current[key as keyof IncidentRequestDto];
                    }
                });
                changes.amount = current.amount;
                // IDs de Objetos
                if (current.driver?.id !== original.driver?.id) {
                    changes.driver = current.driver?.id;
                }
                if (current.responsible?.id !== original.responsible?.id) {
                    changes.responsible = current.responsible?.id;
                }

                // Lógica de Adjustment
                const adjChanged = current.dont_make_adjustment !== original.dont_make_adjustment;
                
                if (adjChanged) {
                    if (current.dont_make_adjustment) {
                        // false -> true
                        changes.adjustment = {};
                    } else {
                        // true -> false
                        changes.adjustment = {
                            adjustment_type: current.adjustment?.adjustment_type,
                            amount: current.adjustment?.amount,
                            driver_message: current.adjustment?.message
                        };
                    }
                } else if (!current.dont_make_adjustment) {
                    // Se mantiene en false, comprobar si han cambiado sus propiedades
                    const currentAdj = current.adjustment;
                    const originalAdj = original.adjustment;
                    
                    const hasAdjChanges = 
                        currentAdj?.adjustment_type !== originalAdj?.adjustment_type ||
                        currentAdj?.amount !== originalAdj?.amount ||
                        currentAdj?.message !== originalAdj?.message;

                    if (hasAdjChanges) {
                        changes.adjustment = {
                            adjustment_type: currentAdj?.adjustment_type,
                            amount: currentAdj?.amount,
                            driver_message: currentAdj?.message
                        };
                    }
                }

                return changes;
            }, 
            setNewIncidentToOriginal: (changes: any) => {
                const original = store.incidentOriginalView();
                const current = store.incidentView();
                if (!original || !current) return;

                // Creamos un clon del original para modificar solo lo necesario
                const newOriginalData = { ...original };

                Object.keys(changes).forEach(key => {
                    if (key === 'adjustment') {
                        // Si en changes 'adjustment' está vacío {}, es que dont_make_adjustment pasó a true
                        if (Object.keys(changes.adjustment).length === 0) {
                            newOriginalData.adjustment = undefined; 
                        } else {
                            // Si tiene datos, copiamos el objeto adjustment actual (con su propiedad 'message')
                            newOriginalData.adjustment = { ...current.adjustment! };
                        }
                    } else if (key === 'driver' || key === 'responsible') {
                        // Para estos campos, changes tiene la ID, pero original necesita el objeto completo
                        (newOriginalData as any)[key] = { ... (current as any)[key] };
                    } else {
                        // Para el resto de campos escalares, tomamos el valor directamente
                        (newOriginalData as any)[key] = changes[key];
                    }
                });

                store.updateState({ 
                    incidentOriginalView: IncidentRequestDto.fromJson(newOriginalData) 
                });
            }, 
            getQuantityUnit: (mainType: string,subtypeValue: string ) => {
                
                if (!mainType || !subtypeValue) {
                return { show: false, unit: '', placeholder: '' };
                }

                const subtype = store.subtypes().find(s => s.value === subtypeValue);
                const subtypeLabel = subtype?.label?.toUpperCase() || '';
                const subtypeValueUpper = subtypeValue.toUpperCase();

                if ([IncidenceMainType.PUNCTUALITY, IncidenceMainType.OPERATIONS_PROCESS].includes(mainType as IncidenceMainType) &&
                (subtypeValueUpper.includes('RETRASO') || subtypeValueUpper.includes('ADELANTO') ||
                    subtypeValueUpper.includes('DELAY') || subtypeValueUpper.includes('ADVANCE') ||
                    subtypeLabel.includes('RETRASO') || subtypeLabel.includes('ADELANTO') || subtypeValueUpper.includes('OP_MISSION_NOT_CLOSED'))) {
                return {
                    show: true,
                    unit: 'horas',
                    placeholder: 'transports.incident-form.step3.quantity-hours-placeholder'
                };
                }

                if (mainType === IncidenceMainType.WRONG_DELIVERY &&
                (subtypeValueUpper.includes('DISTANCIA') || subtypeValueUpper.includes('DISTANCE') ||
                    subtypeLabel.includes('DISTANCIA') || subtypeLabel.includes('DISTANCE'))) {
                return {
                    show: true,
                    unit: 'km',
                    placeholder: 'transports.incident-form.step3.quantity-km-placeholder'
                };
                }

                if (mainType === IncidenceMainType.FUEL &&
                (subtypeValueUpper.includes('NIVEL') || subtypeValueUpper.includes('LEVEL') ||
                    subtypeValueUpper.includes('INFERIOR') || subtypeValueUpper.includes('LOW') ||
                    subtypeLabel.includes('NIVEL') || subtypeLabel.includes('INFERIOR'))) {
                return {
                    show: true,
                    unit: 'litros',
                    placeholder: 'transports.incident-form.step3.quantity-liters-placeholder'
                };
                }

                if (mainType === IncidenceMainType.EXCESS_KILOMETERS &&
                (subtypeValueUpper.includes('EXCEDIDO') || subtypeValueUpper.includes('EXCEEDED') ||
                    subtypeValueUpper.includes('KILOMETROS') || subtypeValueUpper.includes('KILOMETERS') ||
                    subtypeLabel.includes('EXCEDIDO') || subtypeLabel.includes('KILOMETROS'))) {
                return {
                    show: true,
                    unit: 'km',
                    placeholder: 'transports.incident-form.step3.quantity-km-placeholder'
                };
                }

                if (mainType === IncidenceMainType.FINE) {
                return {
                    show: true,
                    unit: 'cantidad',
                    placeholder: 'transports.incident-form.step3.quantity-amount-placeholder'
                };
                }

                if (
                (subtypeValueUpper.includes('OTRO') || subtypeValueUpper.includes('OTHER') ||
                    subtypeValueUpper.includes('IRREGULARIDAD') || subtypeValueUpper.includes('IRREGULARITY') ||
                    subtypeLabel.includes('OTRO') || subtypeLabel.includes('IRREGULARIDAD'))) {
                return {
                    show: true,
                    unit: 'cantidad',
                    placeholder: 'transports.incident-form.step3.quantity-amount-placeholder'
                };
                }

                return { show: false, unit: '', placeholder: '' };
            }
        }
    }),
    withComputed((store) => {
        return {
            quantityInputConfig: computed(() => {
                if(store.incidentView() == null) return { show: false, unit: '', placeholder: '' };
                const mainType = store.incidentView()!.incidence_main_type;
                const subtypeValue = store.incidentView()!.incidence_sub_type;
                return store.getQuantityUnit(mainType, subtypeValue);

                if (!mainType || !subtypeValue) {
                return { show: false, unit: '', placeholder: '' };
                }

                const subtype = store.subtypes().find(s => s.value === subtypeValue);
                const subtypeLabel = subtype?.label?.toUpperCase() || '';
                const subtypeValueUpper = subtypeValue.toUpperCase();

                if (mainType === IncidenceMainType.PUNCTUALITY &&
                (subtypeValueUpper.includes('RETRASO') || subtypeValueUpper.includes('ADELANTO') ||
                    subtypeValueUpper.includes('DELAY') || subtypeValueUpper.includes('ADVANCE') ||
                    subtypeLabel.includes('RETRASO') || subtypeLabel.includes('ADELANTO'))) {
                return {
                    show: true,
                    unit: 'horas',
                    placeholder: 'transports.incident-form.step3.quantity-hours-placeholder'
                };
                }

                if (mainType === IncidenceMainType.WRONG_DELIVERY &&
                (subtypeValueUpper.includes('DISTANCIA') || subtypeValueUpper.includes('DISTANCE') ||
                    subtypeLabel.includes('DISTANCIA') || subtypeLabel.includes('DISTANCE'))) {
                return {
                    show: true,
                    unit: 'km',
                    placeholder: 'transports.incident-form.step3.quantity-km-placeholder'
                };
                }

                if (mainType === IncidenceMainType.FUEL &&
                (subtypeValueUpper.includes('NIVEL') || subtypeValueUpper.includes('LEVEL') ||
                    subtypeValueUpper.includes('INFERIOR') || subtypeValueUpper.includes('LOW') ||
                    subtypeLabel.includes('NIVEL') || subtypeLabel.includes('INFERIOR'))) {
                return {
                    show: true,
                    unit: 'litros',
                    placeholder: 'transports.incident-form.step3.quantity-liters-placeholder'
                };
                }

                if (mainType === IncidenceMainType.EXCESS_KILOMETERS &&
                (subtypeValueUpper.includes('EXCEDIDO') || subtypeValueUpper.includes('EXCEEDED') ||
                    subtypeValueUpper.includes('KILOMETROS') || subtypeValueUpper.includes('KILOMETERS') ||
                    subtypeLabel.includes('EXCEDIDO') || subtypeLabel.includes('KILOMETROS'))) {
                return {
                    show: true,
                    unit: 'km',
                    placeholder: 'transports.incident-form.step3.quantity-km-placeholder'
                };
                }

                if (mainType === IncidenceMainType.FINE) {
                return {
                    show: true,
                    unit: 'cantidad',
                    placeholder: 'transports.incident-form.step3.quantity-amount-placeholder'
                };
                }

                if (
                (subtypeValueUpper.includes('OTRO') || subtypeValueUpper.includes('OTHER') ||
                    subtypeValueUpper.includes('IRREGULARIDAD') || subtypeValueUpper.includes('IRREGULARITY') ||
                    subtypeLabel.includes('OTRO') || subtypeLabel.includes('IRREGULARIDAD'))) {
                return {
                    show: true,
                    unit: 'cantidad',
                    placeholder: 'transports.incident-form.step3.quantity-amount-placeholder'
                };
                }

                return { show: false, unit: '', placeholder: '' };
            })       
        }
    }),
    withMethods((store) => {
        var provider = inject(QualityProvider);
        var incidencesProvider = inject(IncidencesProvider);
        
        return {
            saveIncident: async (chages?: {client_zendesk_link?: string, driver_zendesk_link?: string}) => {
                let changes:any = chages;
                if(changes){
                    for(const key of Object.keys(changes )){
                        if(store.incidentOriginalView()![key as keyof IncidentRequestDto] === changes[key]){    
                            delete changes[key];
                        }
                    }
                }else{
                    changes = store.compareChangesIncident();

                }
                if(Object.keys(changes!).length == 0) return;
                
                if(store.incidentView()!.dont_make_adjustment && store.incidentView()?.adjustment){
                    delete changes.adjustment;
                    changes.dont_make_adjustment = true;
                }
                try {
                    // El payload 'changes' ya tiene los IDs aplanados y el adjustment mapeado
                    const result = await provider.updateIncidence(store.incidentView()?.id!, {
                        ...changes
                    });
                    
                    if(result){
                        // Sincronizamos el estado original con el nuevo estado guardado
                        store.setNewIncidentToOriginal(changes);
                    }
                } catch (error) {
                    throw error;
                }
            }, 
            deleteIncident: async (id: number) => {
                try{
                    const result = await provider.deleteIncidence(id);
                    store.updateState({ incidentView: null });

                }catch{
                    throw new Error('Failed to delete incident');

                }
            },
            saveComment: async (comment: string) => {
                try {
                    const result = await provider.addCommentIncidence(store.incidentView()?.id!, 
                        comment
                    );
                    
                    if(result){
                        try {
                            const res = await provider.getIncidentById(store.incidentView()?.id!);
                            if(res){
                                store.incidentUpdate({
                                    timeline: res.timeline.map((t: any) => new IncidentRequestTimelineDto(t))
                                })
                                //store.loadIncidentById(res);
                            }
                        } catch (error) {
                            throw error;
                        }
                        
                        
                    }
                } catch (error) {
                    throw error;
                }
            },
            saveAdjustment: async (adjutstmen: Partial<CreateAdjustmentDto>, id?: number) => {
                try {

                    const res = await provider.saveAdjustment(adjutstmen!, id);
                    if (res) {
                        store.loadAdjustments();
                        return;
                    }
                    throw new Error("No se pudo guardar el ajuste");
                } catch (error) {
                    store.updateState({ adjustmentView: null , adjustmentOriginalView: null });
                    throw error;
                }
                
            },
            deleteAdjustment: async (id: number) => {
                try {
                    const result = await provider.deleteAdjustment(id);
                        store.loadAdjustments();
                        return;

                } catch (error) {
                    throw error;
                }
            },
        }}),

);



function saveQualityStoreToStorage(store: any, key = 'roadit_quality_store') {
    const result: Record<string, unknown> = {};
    for (const prop of Object.keys(new QualityState)) {
        result[prop] = store[prop]();
    }
    Helpers.setSessionStorage(key, result);

}

// Restaura solo las claves de GlobalStateData, asumiendo que todas son signals
function loadQualityStoreFromStorage(key = 'roadit_quality_store'): QualityState | null {

    return Helpers.getSessionStorage<QualityState>(key);
}




// MOCDATAS

const BLACKLISTDRIVERMOC: BlackListDriverItem[] = [
  {
    "client_id": 1,
    "client_name": "Logística Global S.A.",
    "types": [
      "SIMPLE_MOVEMENT" as TransportPrincipalType, 
      "PICKUP_TO_FINAL_CUSTOMER"  as TransportPrincipalType
    ],
    "subentities": [
      {
        "client_id": 101,
        "client_name": "Delegación Madrid",
        "types": [
          "DELIVERY_TO_FINAL_CUSTOMER" as TransportPrincipalType
        ]
      },
      {
        "client_id": 102,
        "client_name": "Delegación Barcelona",
        "types": [
          "WITH_STOPOVER" as TransportPrincipalType,
          "PICKUP_AND_DELIVERY_TO_FINAL_CUSTOMER" as TransportPrincipalType
        ]
      }
    ]
  },
  {
    "client_id": 5,
    "client_name": "Transportes Rápidos S.L.",
    "types": [
      "SIMPLE_MOVEMENT" as TransportPrincipalType
    ]
  }
]